/**
 * Canvas Utilities - Smart node positioning with collision avoidance
 * 
 * Performance considerations:
 * - Uses spatial grid for O(1) neighbor lookups instead of O(n) collision checks
 * - Grid cell size based on average node dimensions for optimal bucket distribution
 * - Early exit when position is already free
 * - Bounded search to prevent infinite loops
 * - Memoization-friendly pure functions
 */

import { CanvasNode, Position } from '../types';
import { isWorkspaceEditNode } from './workspaceEdit';

// Grid size for snapping (matches InfiniteCanvas GRID_SIZE)
const GRID_SIZE = 20;

// Spatial grid cell size - larger than typical nodes to reduce bucket count
const SPATIAL_CELL_SIZE = 400;

// Maximum search iterations to prevent infinite loops
const MAX_SEARCH_ITERATIONS = 50;

// Padding between nodes
const NODE_PADDING = 20;

// Default node size when not specified
const DEFAULT_NODE_SIZE = { width: 320, height: 200 };

/**
 * Axis-Aligned Bounding Box for collision detection
 */
interface AABB {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Spatial hash grid for efficient collision detection
 * Groups nodes into grid cells for O(1) neighbor lookups
 */
class SpatialGrid {
  private cells: Map<string, AABB[]> = new Map();
  private cellSize: number;

  constructor(cellSize: number = SPATIAL_CELL_SIZE) {
    this.cellSize = cellSize;
  }

  private getCellKey(x: number, y: number): string {
    const cellX = Math.floor(x / this.cellSize);
    const cellY = Math.floor(y / this.cellSize);
    return `${cellX},${cellY}`;
  }

  /**
   * Get all cell keys that an AABB overlaps
   */
  private getCellKeysForAABB(aabb: AABB): string[] {
    const keys: string[] = [];
    const minCellX = Math.floor(aabb.x / this.cellSize);
    const maxCellX = Math.floor((aabb.x + aabb.width) / this.cellSize);
    const minCellY = Math.floor(aabb.y / this.cellSize);
    const maxCellY = Math.floor((aabb.y + aabb.height) / this.cellSize);

    for (let cx = minCellX; cx <= maxCellX; cx++) {
      for (let cy = minCellY; cy <= maxCellY; cy++) {
        keys.push(`${cx},${cy}`);
      }
    }
    return keys;
  }

  /**
   * Add an AABB to the spatial grid
   */
  insert(aabb: AABB): void {
    const keys = this.getCellKeysForAABB(aabb);
    for (const key of keys) {
      if (!this.cells.has(key)) {
        this.cells.set(key, []);
      }
      this.cells.get(key)!.push(aabb);
    }
  }

  /**
   * Get all AABBs that might collide with a given AABB
   * Returns candidates from relevant grid cells (not exact collision check)
   */
  getCandidates(aabb: AABB): AABB[] {
    const keys = this.getCellKeysForAABB(aabb);
    const seen = new Set<AABB>();
    const candidates: AABB[] = [];

    for (const key of keys) {
      const cell = this.cells.get(key);
      if (cell) {
        for (const existingAABB of cell) {
          if (!seen.has(existingAABB)) {
            seen.add(existingAABB);
            candidates.push(existingAABB);
          }
        }
      }
    }
    return candidates;
  }
}

/**
 * Check if two AABBs overlap (with padding)
 */
function aabbOverlap(a: AABB, b: AABB, padding: number = NODE_PADDING): boolean {
  return !(
    a.x + a.width + padding <= b.x ||
    b.x + b.width + padding <= a.x ||
    a.y + a.height + padding <= b.y ||
    b.y + b.height + padding <= a.y
  );
}

/**
 * Snap a value to the grid
 */
function snapToGrid(value: number, gridSize: number = GRID_SIZE): number {
  return Math.round(value / gridSize) * gridSize;
}

/**
 * Convert nodes array to AABBs for collision detection
 */
export function nodesToAABBs(nodes: CanvasNode[]): AABB[] {
  // The Edit page's node is not drawn on the canvas, so it takes up no room there.
  return nodes.filter(node => !isWorkspaceEditNode(node)).map(node => ({
    x: node.position.x,
    y: node.position.y,
    width: node.size?.width || DEFAULT_NODE_SIZE.width,
    height: node.size?.height || DEFAULT_NODE_SIZE.height,
  }));
}

/**
 * Build a spatial grid from node AABBs
 */
function buildSpatialGrid(aabbs: AABB[]): SpatialGrid {
  const grid = new SpatialGrid();
  for (const aabb of aabbs) {
    grid.insert(aabb);
  }
  return grid;
}

/**
 * Check if a position is free (no collision with existing nodes)
 */
function isPositionFree(
  x: number,
  y: number,
  width: number,
  height: number,
  grid: SpatialGrid,
  padding: number = NODE_PADDING
): boolean {
  const testAABB: AABB = { x, y, width, height };
  const candidates = grid.getCandidates(testAABB);
  
  for (const candidate of candidates) {
    if (aabbOverlap(testAABB, candidate, padding)) {
      return false;
    }
  }
  return true;
}

/**
 * Search directions for finding empty positions
 * Strongly prioritizes RIGHT (left-to-right workflow), then down
 */
const SEARCH_DIRECTIONS = [
  { dx: 1, dy: 0, weight: 1 },   // Right (primary direction)
  { dx: 1, dy: 0.5, weight: 2 }, // Right-slight down
  { dx: 1, dy: -0.5, weight: 2 }, // Right-slight up
  { dx: 0, dy: 1, weight: 3 },   // Down
  { dx: 0, dy: -1, weight: 3 },  // Up
  { dx: 1, dy: 1, weight: 4 },   // Right-Down diagonal
  { dx: 1, dy: -1, weight: 4 },  // Right-Up diagonal
  { dx: -1, dy: 0, weight: 5 },  // Left (least preferred)
  { dx: -1, dy: 1, weight: 6 },  // Left-Down diagonal
  { dx: -1, dy: -1, weight: 6 }, // Left-Up diagonal
];

// Compact spacing multiplier - keeps nodes closer together
const COMPACT_SPACING = 0.3; // 30% of node size for tighter packing

/**
 * Find the nearest empty position for a new node
 * Uses tight packing with left-to-right preference
 * 
 * @param startX - Initial X position (canvas coordinates)
 * @param startY - Initial Y position (canvas coordinates)
 * @param nodeWidth - Width of the new node
 * @param nodeHeight - Height of the new node
 * @param existingNodes - Array of existing nodes on the canvas
 * @param options - Additional options
 * @returns Position object with x, y coordinates snapped to grid
 */
export function findEmptyPosition(
  startX: number,
  startY: number,
  nodeWidth: number,
  nodeHeight: number,
  existingNodes: CanvasNode[],
  options: {
    padding?: number;
    maxIterations?: number;
    snapToGrid?: boolean;
    preferredDirection?: 'right' | 'down' | 'left' | 'up';
  } = {}
): Position {
  const {
    padding = NODE_PADDING,
    maxIterations = MAX_SEARCH_ITERATIONS,
    snapToGrid: shouldSnap = true,
    preferredDirection = 'right',
  } = options;

  // Early exit if no existing nodes
  if (existingNodes.length === 0) {
    return {
      x: shouldSnap ? snapToGrid(startX) : startX,
      y: shouldSnap ? snapToGrid(startY) : startY,
    };
  }

  // Build spatial grid for efficient collision detection
  const aabbs = nodesToAABBs(existingNodes);
  const grid = buildSpatialGrid(aabbs);

  // Snap start position to grid
  let testX = shouldSnap ? snapToGrid(startX) : startX;
  let testY = shouldSnap ? snapToGrid(startY) : startY;

  // Check if starting position is already free
  if (isPositionFree(testX, testY, nodeWidth, nodeHeight, grid, padding)) {
    return { x: testX, y: testY };
  }

  // Reorder directions based on preference
  const orderedDirections = getOrderedDirections(preferredDirection);

  // Use smaller step sizes for tighter packing
  // Step = padding + small fraction of node size (keeps nodes close)
  const baseStepX = padding + nodeWidth * COMPACT_SPACING;
  const baseStepY = padding + nodeHeight * COMPACT_SPACING;

  // Search in expanding rings with tight spacing
  for (let distance = 1; distance <= maxIterations; distance++) {
    for (const dir of orderedDirections) {
      // Calculate step based on direction - horizontal moves use width, vertical use height
      const stepX = dir.dx !== 0 ? (padding + nodeWidth) : baseStepY;
      const stepY = dir.dy !== 0 ? (padding + nodeHeight * Math.abs(dir.dy)) : baseStepX;
      
      testX = startX + dir.dx * stepX * distance;
      testY = startY + dir.dy * stepY * distance;

      // Snap to grid
      if (shouldSnap) {
        testX = snapToGrid(testX);
        testY = snapToGrid(testY);
      }

      if (isPositionFree(testX, testY, nodeWidth, nodeHeight, grid, padding)) {
        return { x: testX, y: testY };
      }
    }
  }

  // Fallback: place to the right of all existing nodes (maintains left-to-right flow)
  const maxX = Math.max(...aabbs.map(a => a.x + a.width), startX);
  const fallbackX = shouldSnap ? snapToGrid(maxX + padding) : maxX + padding;
  const fallbackY = shouldSnap ? snapToGrid(startY) : startY;
  
  return { x: fallbackX, y: fallbackY };
}

/**
 * Get search directions ordered by preference
 */
function getOrderedDirections(preferred: 'right' | 'down' | 'left' | 'up') {
  // Clone and sort directions based on preference
  const directions = [...SEARCH_DIRECTIONS];
  
  // Adjust weights based on preferred direction
  if (preferred === 'down') {
    // Swap right and down priorities
    directions.forEach(d => {
      if (d.dx === 0 && d.dy === 1) d.weight = 1;
      else if (d.dx === 1 && d.dy === 0) d.weight = 3;
    });
  } else if (preferred === 'left') {
    // Reverse horizontal preferences
    directions.forEach(d => {
      if (d.dx < 0) d.weight = Math.max(1, d.weight - 4);
      else if (d.dx > 0) d.weight = d.weight + 4;
    });
  } else if (preferred === 'up') {
    directions.forEach(d => {
      if (d.dy < 0) d.weight = Math.max(1, d.weight - 2);
      else if (d.dy > 0) d.weight = d.weight + 2;
    });
  }
  
  return directions.sort((a, b) => a.weight - b.weight);
}

/**
 * Find empty positions for multiple nodes (e.g., spawning from script)
 * Places nodes sequentially, each position considering previously placed nodes
 * Default: horizontal (left-to-right) layout for natural workflow
 * 
 * @param startX - Initial X position
 * @param startY - Initial Y position
 * @param nodeSizes - Array of node sizes to place
 * @param existingNodes - Array of existing nodes
 * @param layout - Layout direction: 'vertical' or 'horizontal' or 'grid'
 * @returns Array of positions for each node
 */
export function findEmptyPositionsForMultiple(
  startX: number,
  startY: number,
  nodeSizes: Array<{ width: number; height: number }>,
  existingNodes: CanvasNode[],
  layout: 'vertical' | 'horizontal' | 'grid' = 'horizontal', // Changed default to horizontal
  gridColumns: number = 6 // More columns for horizontal flow
): Position[] {
  const positions: Position[] = [];
  
  // Create a temporary list of "virtual" nodes that includes existing + already placed
  let virtualNodes = [...existingNodes];
  
  let currentX = startX;
  let currentY = startY;
  let columnIndex = 0;
  let rowMaxHeight = 0;

  for (let i = 0; i < nodeSizes.length; i++) {
    const size = nodeSizes[i];
    
    // Determine preferred direction based on layout
    const preferredDirection = layout === 'vertical' ? 'down' : 'right';
    
    // Find empty position for this node
    const position = findEmptyPosition(
      currentX,
      currentY,
      size.width,
      size.height,
      virtualNodes,
      { preferredDirection }
    );
    
    positions.push(position);
    
    // Add a virtual node at this position for subsequent placements
    virtualNodes = [...virtualNodes, {
      id: `virtual-${i}`,
      type: 0 as any, // Type doesn't matter for collision
      position,
      size,
      data: {},
    } as CanvasNode];
    
    // Update next search position based on layout
    if (layout === 'vertical') {
      currentY = position.y + size.height + NODE_PADDING;
    } else if (layout === 'horizontal') {
      currentX = position.x + size.width + NODE_PADDING;
    } else if (layout === 'grid') {
      columnIndex++;
      if (columnIndex >= gridColumns) {
        columnIndex = 0;
        currentX = startX;
        currentY += rowMaxHeight + NODE_PADDING;
        rowMaxHeight = 0;
      } else {
        currentX = position.x + size.width + NODE_PADDING;
      }
      rowMaxHeight = Math.max(rowMaxHeight, size.height);
    }
  }
  
  return positions;
}

/**
 * Check if a drop position overlaps with any existing node
 * Used to determine if collision avoidance is needed
 */
export function positionOverlapsNodes(
  x: number,
  y: number,
  width: number,
  height: number,
  existingNodes: CanvasNode[]
): boolean {
  if (existingNodes.length === 0) return false;
  
  const aabbs = nodesToAABBs(existingNodes);
  const grid = buildSpatialGrid(aabbs);
  
  return !isPositionFree(x, y, width, height, grid, NODE_PADDING);
}

/**
 * Default node sizes by type - for reference when adding nodes
 */
export const NODE_SIZES: Record<string, { width: number; height: number }> = {
  IMAGE_VIEWER: { width: 320, height: 320 },
  VIDEO_GENERATOR: { width: 400, height: 620 },
  IMAGE_GENERATOR: { width: 350, height: 500 },
  SKETCH_PAD: { width: 400, height: 490 },
  SCRIPT_PROCESSOR: { width: 350, height: 250 },
  TEXT_PROMPT: { width: 320, height: 200 },
  WRITE_NODE: { width: 340, height: 420 },
  CHARACTER_NODE: { width: 300, height: 450 },
  PROP_NODE: { width: 300, height: 450 },
  LOCATION_NODE: { width: 300, height: 450 },
  STORAGE_BROWSER: { width: 400, height: 450 },
  STORYBOARD_NODE: { width: 1200, height: 700 },
  SHARE_NODE: { width: 380, height: 450 },
};

/**
 * Get default size for a node type
 */
export function getDefaultNodeSize(nodeType: string): { width: number; height: number } {
  return NODE_SIZES[nodeType] || DEFAULT_NODE_SIZE;
}

/**
 * Smooth animation parameters
 */
const ANIMATION_DURATION_MS = 400;
const ANIMATION_EASING = (t: number): number => {
  // Ease-out cubic for smooth deceleration
  return 1 - Math.pow(1 - t, 3);
};

/**
 * Calculate the transform needed to center a node position in the viewport
 * 
 * @param nodeX - Node X position (canvas coordinates)
 * @param nodeY - Node Y position (canvas coordinates)
 * @param nodeWidth - Node width
 * @param nodeHeight - Node height
 * @param viewportWidth - Viewport width (typically window.innerWidth)
 * @param viewportHeight - Viewport height (typically window.innerHeight)
 * @param currentScale - Current zoom scale
 * @returns New transform x, y values to center the node
 */
export function calculateCenterTransform(
  nodeX: number,
  nodeY: number,
  nodeWidth: number,
  nodeHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  currentScale: number
): { x: number; y: number } {
  // Calculate the center of the node in canvas coordinates
  const nodeCenterX = nodeX + nodeWidth / 2;
  const nodeCenterY = nodeY + nodeHeight / 2;
  
  // Calculate the transform to place node center at viewport center
  // viewport_center = transform + canvas_pos * scale
  // So: transform = viewport_center - canvas_pos * scale
  const newX = viewportWidth / 2 - nodeCenterX * currentScale;
  const newY = viewportHeight / 2 - nodeCenterY * currentScale;
  
  return { x: newX, y: newY };
}

/**
 * Create a smooth pan animation to center a node in the viewport
 * 
 * @param nodeX - Node X position (canvas coordinates)
 * @param nodeY - Node Y position (canvas coordinates)
 * @param nodeWidth - Node width
 * @param nodeHeight - Node height
 * @param currentTransform - Current viewport transform
 * @param onUpdate - Callback called each animation frame with new transform
 * @param onComplete - Callback called when animation completes
 * @returns Cancel function to stop the animation
 */
export function animatePanToNode(
  nodeX: number,
  nodeY: number,
  nodeWidth: number,
  nodeHeight: number,
  currentTransform: { x: number; y: number; scale: number },
  onUpdate: (transform: { x: number; y: number; scale: number }) => void,
  onComplete?: () => void
): () => void {
  const startTransform = { ...currentTransform };
  const targetTransform = calculateCenterTransform(
    nodeX,
    nodeY,
    nodeWidth,
    nodeHeight,
    window.innerWidth,
    window.innerHeight,
    currentTransform.scale
  );
  
  // Check if we need to animate (significant distance)
  const dx = targetTransform.x - startTransform.x;
  const dy = targetTransform.y - startTransform.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  // Skip animation if node is already mostly centered (within 50px)
  if (distance < 50) {
    onComplete?.();
    return () => {};
  }
  
  let animationFrameId: number | null = null;
  let startTime: number | null = null;
  let cancelled = false;
  
  const animate = (timestamp: number) => {
    if (cancelled) return;
    
    if (startTime === null) {
      startTime = timestamp;
    }
    
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / ANIMATION_DURATION_MS, 1);
    const easedProgress = ANIMATION_EASING(progress);
    
    const newX = startTransform.x + dx * easedProgress;
    const newY = startTransform.y + dy * easedProgress;
    
    onUpdate({
      x: newX,
      y: newY,
      scale: currentTransform.scale,
    });
    
    if (progress < 1) {
      animationFrameId = requestAnimationFrame(animate);
    } else {
      onComplete?.();
    }
  };
  
  animationFrameId = requestAnimationFrame(animate);
  
  // Return cancel function
  return () => {
    cancelled = true;
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
    }
  };
}
